import { Component, Vue } from "vue-property-decorator";
import axios, { AxiosError } from "axios";
import { Token } from '@atabix/core';
import { User } from "@/models/User";
import LanguageSwitch from "@/components/language-switch/LanguageSwitch";
import ErrorHandler from "@/support/ErrorHandler";
import { identifyHostname } from "@/support/Client";
import MiButton from "@/components/mi-button/MiButton";
import { loadLanguageAsync } from '@/i18n';

@Component<Login>({
  components: {
    LanguageSwitch,
    MiButton
  },
  beforeRouteUpdate(to, from, next) {
    next();

    this.$route.name === "logout" ? this.logout() : null;
  }
})
export default class Login extends Vue {
  public $pageTitle!: string;

  public oAuthServer: string = identifyHostname() || "";
  public oAuthClientId: number = 1;
  public oAuthSecret: string = "At4secret";
  public oAuthScope: string = "*";

  public username: string = "";
  public password: string = "";
  public token: string = "";
  public loginFormValid: boolean = false;
  public hasToken = false;

  public mounted() {
    if (Token.get("refresh")) {
      this.login("refresh");
    }
    this.$pageTitle = this.$t("auth.login").toString();
    this.$route.name === "logout" ? this.logout() : null;
  }

  /**
   * login constructs the payload and sends it to the oAuth server
   * and handles the oAuth responses (correct / incorrect login)
   * sets user to logged in, if so.
   *
   * @param {string} loginType
   * @memberof Login
   */
  // @TODO rename this. login.login() is bad
  public login(loginType: string) {
    let payload: AuthenticationPayload = {
      client_id: this.oAuthClientId,
      client_secret: this.oAuthSecret,
      scope: this.oAuthScope,
      grant_type:
        loginType && loginType === "refresh" ? "refresh_token" : "password"
    };

    if (loginType && loginType === "refresh") {
      payload.refresh_token = Token.get("refresh");
    } else {
      payload.username = this.username;
      payload.password = this.password;
    }

    if (this.token) {
      payload.token = this.token;
    }

    let headers = {};

    if (window.location.hostname !== "localhost") {
      const application = window.location.host.split(".")[0];
      (headers as any)["X-Application"] = application;
    }

    const client = axios.create({
      baseURL: this.oAuthServer,
      headers
    });

    client
      .post("/oauth/token", payload)
      .then(response => {
        if (response.data.access_token) {
          Token.delete(["access", "refresh"]);
          Token.set({
            access: response.data.access_token,
            refresh: response.data.refresh_token
          });
          new User()
            .current()
            .then((user: User) => {
              this.$store.dispatch("userAuthenticated", user);
              loadLanguageAsync(user.language || 'en')
              this.redirect(user);
            })
            .catch((error: AxiosError) => {
              console.log("error", error);
              Token.delete(["access", "refresh"]);
            });
        }
      })
      .catch((error: AxiosError) => {
        if (! this.hasToken && loginType && loginType !== "refresh" && error.response && error.response.data && error.response.data.hint === "Invalid MFA token.") {
          this.hasToken = true;

          return;
        }
        if (loginType && loginType !== "refresh") {
          ErrorHandler.alert(
            "The provided information was incorrect.",
            "Invalid Credentials"
          );
        }
      });
  }

  public logout() {
    this.$store.dispatch("userDeauthenticated");
    this.$router.push("/login");
  }

  public redirect(user: User) {
    // If the user has not set their mfa token yet, we redirect them to their account page (which shows a warning)
    if (! user.has_mfa) {
      this.$router.push(`/admin/${user.id}`);

      return;
    }

    if (this.$route.query.redirect) {
      this.$router.push(this.$route.query.redirect.toString());

      return;
    }

    if (!this.$store.state.Auth.canViewLeads()) {
      this.$router.push("/orders");
      return;
    }

    this.$router.push("/leads");
  }

  get emailRules() {
    return [(v: string) => !!v || this.$t("auth.required.email").toString()];
    //, (v: string) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('auth.required.email').toString()
  }

  get passwordRules() {
    return [(v: string) => !!v || this.$t("auth.required.password").toString()];
  }

  get tokenRules() {
    return true;
  }
}

interface AuthenticationPayload {
  username?: string;
  password?: string;
  token?: string;
  refresh_token?: string;
  grant_type: string;
  client_id: number;
  client_secret: string;
  scope: string;
}
